import { CostCodes, Data, Menu, Modal, SubscriptionPlan, Utils } from 'common';
import isArray from 'lodash/isArray';
import isNumber from 'lodash/isNumber';
import uniqueId from 'lodash/uniqueId';
import React, { useContext } from 'react';
import AddEditGroupModal from './groups/AddEditGroupModal.component';
import DeleteGroupModal from './groups/DeleteGroupModal.component';
import ManageGroupsModal from './groups/ManageGroupsModal.component';
import ActiveItemMenuId from './utils/ActiveItemMenuId.enum';

const { ActionType, ItemType, MenuPanel } = Menu;
const { ManageCostCodesModal } = CostCodes;
const { ModalContext, ModalButtonActionType } = Modal;
const { getUpgradeIconProps, isCostCodesGated, showUpgradePlanModal } =
	SubscriptionPlan;
const { Logger, Session } = Utils;
const { PlanIds } = Data;

const ContactsLeftMenu = ({
	counts,
	groups,
	isModal,
	costCodes,
	activeMenuItem,
	getCostCodes,
	fetchContacts,
	onMenuItemClick,
	updateContactsState,
}) => {
	const { showModal } = useContext(ModalContext);
	const costCodesGated = isCostCodesGated();
	const costCodesUpgradeProps = costCodesGated
		? { showUpgradeIcon: true }
		: getUpgradeIconProps('costCodes', { showModal });

	const menuItems = [
		{
			id: parseInt(uniqueId(), 10),
			type: ItemType.Search,
			title: 'Search',
			data: {
				activeMenuItemId: ActiveItemMenuId.Search,
			},
			dataqa: 'contacts-left-menu-search',
		},
		{
			id: parseInt(uniqueId(), 10),
			type: ItemType.Badge,
			title: 'Selected',
			active: activeMenuItem.activeMenuItemId === ActiveItemMenuId.Selected,
			showBadge: true,
			badge: counts.selected,
			data: {
				activeMenuItemId: ActiveItemMenuId.Selected,
				leftMenuTitle: 'All Selected Contacts',
			},
			dataqa: 'contacts-left-menu-selected',
		},
		{
			id: parseInt(uniqueId(), 10),
			type: ItemType.Badge,
			title: 'All Contacts',
			active: activeMenuItem.activeMenuItemId === ActiveItemMenuId.All,
			showBadge: true,
			badge: counts.all,
			data: {
				activeMenuItemId: ActiveItemMenuId.All,
				leftMenuTitle: 'All Contacts',
			},
			dataqa: 'contacts-left-menu-all-contacts',
		},
		{
			id: parseInt(uniqueId(), 10),
			type: ItemType.Group,
			title: 'Groups',
			active: activeMenuItem.activeMenuItemId === ActiveItemMenuId.Groups,
			collapsed: activeMenuItem.activeMenuItemId !== ActiveItemMenuId.Groups,
			showTools: !isModal,
			items: isArray(groups)
				? groups.map(item => ({
						id: parseInt(uniqueId(), 10),
						type: ItemType.Badge,
						title: item.name,
						active:
							activeMenuItem.activeMenuItemId === ActiveItemMenuId.Groups &&
							activeMenuItem.activeMenuItemValue === item.id,
						showTools: item.name !== 'Ungrouped' && !isModal,
						showBadge: true,
						badge: item.count || 0,
						data: {
							activeMenuItemId: ActiveItemMenuId.Groups,
							activeMenuItemValue: item.id,
							leftMenuTitle: `All ${item.name} Contacts`,
							activeMenuItemName: item.name,
						},
						dataqa: `contacts-left-menu-groups-${item.name}`,
					}))
				: [],
			dataqa: 'contacts-left-menu-groups',
		},
		{
			id: parseInt(uniqueId(), 10),
			type: ItemType.Group,
			title: 'Cost Codes',
			active: activeMenuItem.activeMenuItemId === ActiveItemMenuId.CostCodes,
			collapsed: activeMenuItem.activeMenuItemId !== ActiveItemMenuId.CostCodes,
			showTools: !isModal && !costCodesGated,
			...costCodesUpgradeProps,
			...(costCodesGated
				? { onHeaderClick: () => showUpgradePlanModal(showModal) }
				: {}),
			items: isArray(costCodes)
				? costCodes.map(item => ({
						id: parseInt(uniqueId(), 10),
						type: ItemType.Badge,
						title: item.name,
						active:
							activeMenuItem.activeMenuItemId === ActiveItemMenuId.CostCodes &&
							activeMenuItem.activeMenuItemValue === item.id,
						showBadge: true,
						badge: item.count || 0,
						data: {
							activeMenuItemId: ActiveItemMenuId.CostCodes,
							activeMenuItemValue: item.id,
							leftMenuTitle: `All ${item.name} Contacts`,
							activeMenuItemName: item.name,
						},
						dataqa: `contacts-left-menu-cost-codes-${item.name}`,
					}))
				: [],
			dataqa: 'contacts-left-menu-cost-codes',
		},
	];

	const showResult =
		activeMenuItem.activeMenuItemId === ActiveItemMenuId.Search;

	const resultCount = showResult ? counts.search : 0;

	const closeCallback = (closeType, isSuccessful) => {
		if (isSuccessful) {
			fetchContacts();
		}
	};

	const handleMenuItemClick = (actionType, itemType, data) => {
		const selectedMenuItemId = isNumber(data.groupId) ? data.groupId : data.id;

		// this condition handles all actions associated with searching
		if (itemType === ItemType.Search) {
			if (actionType === ActionType.Delete) {
				// clicking "X" should switch to filter by "All"
				onMenuItemClick({
					activeMenuItemId: ActiveItemMenuId.All,
					leftMenuTitle: 'All Contacts',
				});
			} else {
				onMenuItemClick({
					activeMenuItemId: ActiveItemMenuId.Search,
					activeMenuItemValue: data.term,
					leftMenuTitle: `All Contacts Searched By ${data.term || ''}`,
				});

				Logger.logToServer({
					category: 'search_info',
					newValue: data.term,
				});
			}
		}
		// this condition handles all badge clicks
		else if (itemType === ItemType.Badge) {
			if (actionType === ActionType.Edit) {
				showModal({
					component: AddEditGroupModal,
					data: {
						groups,
						selectedGroup: {
							id: data.activeMenuItemValue,
							name: data.activeMenuItemName,
						},
						action: 'Edit',
					},
					closeCallback,
				});

				Logger.logToServer({
					category: 'manage_groups_edit',
					action: 'click',
					action_location: 'left_panel',
				});
			} else if (actionType === ActionType.Delete) {
				showModal({
					component: DeleteGroupModal,
					data: {
						selectedGroup: {
							id: data.activeMenuItemValue,
							name: data.activeMenuItemName,
						},
					},
					closeCallback: () => {
						updateContactsState({
							refreshGroupUpdate: true,
						});
					},
				});

				Logger.logToServer({
					category: 'manage_groups_delete',
					action: 'click',
					action_location: 'left_panel',
				});
			} else {
				onMenuItemClick(data);

				if (
					data.activeMenuItemId === ActiveItemMenuId.Groups ||
					data.activeMenuItemId === ActiveItemMenuId.CostCodes
				) {
					Logger.logToServer({
						category: 'filter_change',
						action: 'contacts',
						newFilter: `${
							data.activeMenuItemId.includes('groups')
								? 'groupName'
								: 'costcodes'
						}_${data.leftMenuTitle}`,
					});
				}
			}
		}
		// this condition handles all tool button clicks on group button
		else if (itemType === ItemType.Group) {
			const selectedMenuItemData =
				menuItems.find(item => item.id === selectedMenuItemId) || {};

			// cog wheel button
			if (actionType === ActionType.Settings) {
				if (selectedMenuItemData.title === 'Cost Codes') {
					if (costCodesGated) {
						showUpgradePlanModal(showModal);
						return;
					}
					showModal({
						component: ManageCostCodesModal,
						data: {
							callback: getCostCodes,
						},
					});

					Logger.logToServer({
						category: 'costcode_add_edit',
						action: 'click',
						action_location: 'left_panel',
					});
				} else if (selectedMenuItemData.title === 'Groups') {
					showModal({
						component: ManageGroupsModal,
						data: {
							groups,
							callBack: fetchContacts,
						},
					});

					Logger.logToServer({
						category: 'groups_add_edit',
						action: 'click',
						action_location: 'left_panel',
					});
				}
			}
			// plus sign button
			else if (
				actionType === ActionType.Add &&
				selectedMenuItemData.title === 'Groups'
			) {
				showModal({
					component: AddEditGroupModal,
					data: {
						groups,
						action: 'Add',
					},
					closeCallback,
				});

				Logger.logToServer({
					category: 'manage_groups_add',
					action: 'click',
					action_location: 'left_panel',
				});
			}
		}
	};

	return (
		<div className="contacts-leftmenu common-menu">
			<MenuPanel
				items={menuItems}
				showResult={showResult}
				resultCount={resultCount}
				onActionTrigger={handleMenuItemClick}
			/>
		</div>
	);
};

export default ContactsLeftMenu;
